import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';

import {isNotNil} from 'ramda';
import {isPositive, isNonPositive} from 'ramda-adjunct';

import {DocumentContextCountResponseBody, GetTireSetCountApiResponse} from '@dms/api/core';
import {GetVehicleCustomerApiResponse} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {
  GetMetadaServiceCaseApiResponse,
  GetPostponeJobsApiResponse,
  GetServiceCaseOrderCountApiResponse,
  useGetServiceCaseByVehicleCountQuery,
} from '@dms/api/workshop';
import featureFlags from '@dms/feature-flags';
import {GDPRCollector} from '@dms/features/document-context';
import {TaskGridPage, useTasksCount} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  Checklists,
  OwnershipSection,
  Section,
  SectionItem,
  workshopSearchParams,
  useChecklist,
} from '@dms/shared';

import {buildArray} from 'shared/web';

import {ActiveServiceCases} from '../(sections)/ActiveServiceCases/ActiveServiceCases';
import {ActivityLogs} from '../(sections)/ActivityLogs/ActivityLogs';
import {CheckinHandover} from '../(sections)/CheckinHandover/CheckinHandover';
import {ServiceCaseDocuments} from '../(sections)/Documents/ServiceCaseDocuments';
import {HistoryOfRepairs} from '../(sections)/HistoryOfRepairs/HistoryOfRepairs';
import {Orders} from '../(sections)/Order/Orders';
import {Overview} from '../(sections)/Overview/Overview';
import {Photos} from '../(sections)/Photos/Photos';
import {Postponed} from '../(sections)/PostponedRequests/Postponed';
import {VehicleCampaigns} from '../(sections)/ServiceCampaigns/VehicleCampaigns';
import {ServiceIntervals} from '../(sections)/ServiceIntervals/ServiceIntervals';
import {TiresOverview} from '../(sections)/Tires/TiresOverview';
import {Vehicle} from '../(sections)/Vehicle/Vehicle';

interface UseServiceCaseDetailSectionsProps {
  serviceCaseId: string;
  activeServiceCampaignsLength: number;
  canReadTasks: boolean;
  canReadServiceCase: boolean;
  canReadPneuhotel: boolean;
  serviceCase: GetMetadaServiceCaseApiResponse;
  serviceCaseOrderCount: GetServiceCaseOrderCountApiResponse;
  documentCount: DocumentContextCountResponseBody | undefined;
  vehicleCustomers: GetVehicleCustomerApiResponse;
  vehicleTires: GetTireSetCountApiResponse;
  postponedJobs: GetPostponeJobsApiResponse;
}

export function useServiceCaseDetailSections(props: UseServiceCaseDetailSectionsProps) {
  const isServiceCaseReadOnly = props.serviceCase?.state === 'CANCELLED';
  const activeServiceCasesCountApiArg = isNotNil(props.serviceCase?.vehicleId)
    ? {
        vehicleId: props.serviceCase.vehicleId,
        onlyActive: true,
      }
    : skipToken;
  const {data: activeServiceCasesCount} = useGetServiceCaseByVehicleCountQuery(
    activeServiceCasesCountApiArg
  );

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: props.serviceCaseId,
    resourceId: 'SERVICE_CASE',
  });

  const {count: tasksTodoCount} = useTasksCount({
    entityId: props.serviceCaseId,
    relation: EntityResourceIds.serviceCase,
    state: 'TODO',
  });
  const {count: tasksInProgressCount} = useTasksCount({
    entityId: props.serviceCaseId,
    relation: EntityResourceIds.serviceCase,
    state: 'IN_PROGRESS',
  });
  const tasksCountBadge = tasksTodoCount + tasksInProgressCount;

  return buildArray<SectionItem>([
    {
      id: workshopSearchParams.serviceDetail.overview,
      label: i18n.t('entity.serviceCase.labels.overview'),
      content: <Overview />,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-overview'),
    },
  ])
    .add({
      id: workshopSearchParams.serviceDetail.orders,
      label: i18n.t('entity.serviceCase.labels.orders'),
      content: <Orders />,
      badge: isPositive(props.serviceCaseOrderCount?.count)
        ? {value: String(props.serviceCaseOrderCount?.count ?? ''), colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-orders'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.checkinHandover,
      label: i18n.t('entity.workshop.labels.checkinAndHandover'),
      content: (
        <CheckinHandover
          vehicleId={props.serviceCase?.vehicleId ?? ''}
          isReadOnly={isServiceCaseReadOnly}
        />
      ),
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-checkinHandover'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.documents,
      label: i18n.t('general.labels.documents'),
      content: (
        <ServiceCaseDocuments
          vehicleId={props.serviceCase?.vehicleId}
          isReadOnly={isServiceCaseReadOnly}
        />
      ),
      badge: !!props.documentCount?.count
        ? {value: props.documentCount.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-documents'),
    })
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: workshopSearchParams.serviceDetail.checklists,
      label: i18n.t('entity.checklist.labels.title'),
      content: (
        <Section>
          <Checklists
            {...checklistSettings}
            contextTarget="service-case"
            contextId={props.serviceCaseId}
            isReadOnly={isServiceCaseReadOnly}
          />
        </Section>
      ),
      badge: badgeSettings,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-checklists'),
    })
    .when(isFeatureEnabled(featureFlags.CORE_TASK_MANAGEMENT) && props.canReadTasks, {
      id: 'tasks',
      label: i18n.t('entity.task.labels.tasks'),
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-tasks'),
      badge: {
        colorScheme: 'neutral',
        value: tasksCountBadge,
      },
      content: (
        <TaskGridPage
          gridCode="service-case-task"
          entityId={props.serviceCaseId}
          isReadOnly={isServiceCaseReadOnly}
          autoHeight={false}
          data-testid={testIds.workshop.serviceCaseDetail('tasks')}
        />
      ),
    })
    .add({
      id: workshopSearchParams.serviceDetail.photos,
      label: i18n.t('entity.photo.labels.photos'),
      content: <Photos isReadOnly={isServiceCaseReadOnly} />,
      hasSeparator: true,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-photos'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.vehicle,
      label: i18n.t('entity.vehicle.labels.vehicle'),
      content: <Vehicle />,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-vehicle'),
    })
    .when(props.canReadServiceCase, {
      id: workshopSearchParams.serviceDetail.ownership,
      label: i18n.t('entity.serviceCase.labels.ownership'),
      content: (
        <OwnershipSection
          vehicleId={props.serviceCase?.vehicleId ?? ''}
          additionalTab={{
            queryId: 'gdprConsents',
            title: i18n.t('entity.customer.labels.GDPRConsents'),
            content: <GDPRCollector vehicleId={props.serviceCase?.vehicleId ?? ''} />,
          }}
        />
      ),
      badge: {
        colorScheme: 'neutral',
        value: props.vehicleCustomers?.length ?? 0,
      },
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-ownership'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.postponed,
      label: i18n.t('entity.serviceCase.labels.postponed'),
      content: <Postponed />,
      badge: isPositive(props.postponedJobs?.count)
        ? {value: String(props.postponedJobs?.count ?? ''), colorScheme: 'neutral'}
        : undefined,
      isDisabled: isNonPositive(props.postponedJobs?.count),
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-postponed'),
    })
    .when(props.canReadPneuhotel && isNotNil(props.serviceCase?.vehicleId), {
      id: workshopSearchParams.serviceDetail.tires,
      label: i18n.t('page.tiresInventory.labels.tires'),
      badge: isPositive(props.vehicleTires?.count)
        ? {value: String(props.vehicleTires?.count ?? ''), colorScheme: 'neutral'}
        : undefined,
      content: (
        <TiresOverview
          vehicleId={props.serviceCase?.vehicleId ?? ''}
          customerId={props.serviceCase?.customerId ?? ''}
        />
      ),
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-tires'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.serviceIntervals,
      label: i18n.t('entity.vehicle.labels.serviceIntervals'),
      content: (
        <ServiceIntervals
          vehicleId={props.serviceCase?.vehicleId}
          serviceCaseId={props.serviceCase?.id}
        />
      ),
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-serviceIntervals'),
    })
    .when(isNotNil(props.serviceCase?.vehicleId), {
      id: workshopSearchParams.serviceDetail.activeServiceCases,
      label: i18n.t('entity.serviceCase.labels.activeServiceCases'),
      content: <ActiveServiceCases vehicleId={props.serviceCase?.vehicleId ?? ''} />,
      badge: isPositive(activeServiceCasesCount?.count)
        ? {value: String(activeServiceCasesCount?.count ?? ''), colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-activeServiceCases'),
    })
    .when(isNotNil(props.serviceCase?.vehicleId), {
      id: workshopSearchParams.serviceDetail.historyOfRepairs,
      label: i18n.t('entity.serviceCase.labels.historyOfRepairs'),
      content: <HistoryOfRepairs vehicleId={props.serviceCase?.vehicleId ?? ''} />,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-historyOfRepairs'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.serviceCampaigns,
      label: i18n.t('entity.campaigns.labels.serviceCampaigns'),
      content: <VehicleCampaigns vehicleId={props.serviceCase?.vehicleId} />,
      badge: isPositive(props.activeServiceCampaignsLength)
        ? {colorScheme: 'neutral', value: props.activeServiceCampaignsLength}
        : undefined,
      isDisabled: isNonPositive(props.activeServiceCampaignsLength),
      hasSeparator: true,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-serviceCampaigns'),
    })
    .add({
      id: workshopSearchParams.serviceDetail.activityLogs,
      label: i18n.t('general.labels.activityLogs'),
      content: <ActivityLogs />,
      'data-testid': testIds.workshop.serviceCaseDetail('navigation-activityLogs'),
    });
}
